import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { EnvelopeSimple, Lock, UserCircle, MapPin, Gift } from '@phosphor-icons/react';

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await register(email, password, name, referralCode);
      navigate('/explore');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4 py-12 bg-[#F9F9F6]" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-[#FF6B35] border-2 border-[#004E89] flex items-center justify-center mx-auto mb-4 shadow-[4px_4px_0px_#004E89]">
            <MapPin size={32} weight="fill" color="white" />
          </div>
          <h1 className="text-3xl font-black text-[#004E89] mb-2" style={{ fontFamily: 'Outfit' }}>Join SaleSeeker</h1>
          <p className="text-[#5C7A99] text-sm">Create a free account to start exploring</p>
          <div className="inline-flex items-center gap-1.5 bg-[#4CB944] bg-opacity-15 border border-[#4CB944] text-[#004E89] rounded-full px-3 py-1 mt-3" data-testid="register-free-badge">
            <span className="text-xs font-bold">Always FREE for shoppers</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border-2 border-[#004E89] rounded-2xl p-8 shadow-[4px_4px_0px_#004E89]" data-testid="register-form">
          {error && (
            <div className="bg-red-50 border-2 border-red-400 rounded-xl p-3 mb-4 text-red-700 text-sm font-medium" data-testid="register-error">
              {error}
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-bold text-[#004E89] mb-2 uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Name</label>
            <div className="relative">
              <UserCircle size={20} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C7A99]" />
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                className="input-neo pl-12" placeholder="Your name" required data-testid="register-name-input"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-bold text-[#004E89] mb-2 uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Email</label>
            <div className="relative">
              <EnvelopeSimple size={20} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C7A99]" />
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="input-neo pl-12" placeholder="you@example.com" required data-testid="register-email-input"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-bold text-[#004E89] mb-2 uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Password</label>
            <div className="relative">
              <Lock size={20} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C7A99]" />
              <input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="input-neo pl-12" placeholder="Min 6 characters" required data-testid="register-password-input"
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-bold text-[#004E89] mb-2 uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>
              <span className="flex items-center gap-1">
                <Gift size={14} weight="bold" className="text-[#FF6B35]" />
                Referral Code <span className="text-[#5C7A99] font-normal normal-case">(optional)</span>
              </span>
            </label>
            <input
              type="text" value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              className="input-neo uppercase tracking-wider text-sm" placeholder="e.g. REF-JOHN-A1B2"
              data-testid="register-referral-input"
            />
            {referralCode && (
              <p className="text-xs text-[#4CB944] mt-1 font-medium">Both you and the referrer will get 1 free listing!</p>
            )}
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full py-3 text-lg" data-testid="register-submit-button">
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>

          <p className="text-center text-sm text-[#5C7A99] mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-[#FF6B35] font-bold hover:underline" data-testid="register-to-login-link">
              Sign In
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
